import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import TopNav from "@/components/TopNav";
import Footer from "@/components/Footer";
import ModuleAccent from "@/components/ModuleAccent";
import { motion } from "framer-motion";
import {
    Sparkles, Flame, Sun, Snowflake, Target, Phone, Tag, TrendingUp,
    Inbox, Calendar, Network, CheckCircle2, RefreshCw, ArrowUpRight,
    AlertCircle, ChevronRight, Filter, X
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const baseStyle = { background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" };

export default function AriaDashboard() {
    const { user } = useAuth();
    const [days, setDays] = useState(7);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [leads, setLeads] = useState([]);
    const [leadsLoading, setLeadsLoading] = useState(false);
    const [intentFilter, setIntentFilter] = useState("");
    const [tagFilter, setTagFilter] = useState("");
    const [goalOnly, setGoalOnly] = useState(false);
    const [contacting, setContacting] = useState(null);

    useEffect(() => { document.title = "Dashboard Aria — TotalKlick"; }, []);

    useEffect(() => {
        if (!user) return;
        let alive = true;
        (async () => {
            setStatsLoading(true);
            try {
                const res = await api.get(`/widget/stats?days=${days}`);
                if (alive) setStats(res.data);
            } catch (e) { /* silent */ }
            finally { if (alive) setStatsLoading(false); }
        })();
        return () => { alive = false; };
    }, [user, days]);

    useEffect(() => {
        if (!user) return;
        let alive = true;
        (async () => {
            setLeadsLoading(true);
            try {
                const qs = new URLSearchParams({ limit: "100", since_days: String(days) });
                if (intentFilter) qs.set("intent", intentFilter);
                if (tagFilter) qs.set("tag", tagFilter);
                if (goalOnly) qs.set("goal_only", "true");
                const res = await api.get(`/widget/leads?${qs.toString()}`);
                if (alive) setLeads(res.data || []);
            } catch (e) { /* silent */ }
            finally { if (alive) setLeadsLoading(false); }
        })();
        return () => { alive = false; };
    }, [user, days, intentFilter, tagFilter, goalOnly]);

    const markContacted = async (leadId) => {
        setContacting(leadId);
        try {
            await api.post(`/widget/leads/${leadId}/mark-contacted`);
            // refresh stats + leads
            const [s, l] = await Promise.all([
                api.get(`/widget/stats?days=${days}`),
                api.get(`/widget/leads?limit=100&since_days=${days}${intentFilter ? `&intent=${intentFilter}` : ""}${tagFilter ? `&tag=${tagFilter}` : ""}${goalOnly ? "&goal_only=true" : ""}`),
            ]);
            setStats(s.data); setLeads(l.data || []);
        } catch (e) { /* silent */ }
        finally { setContacting(null); }
    };

    const clearFilters = () => { setIntentFilter(""); setTagFilter(""); setGoalOnly(false); };

    if (!user) {
        return (
            <ModuleAccent module="aria" as="div" className="min-h-screen" style={baseStyle}>
                <TopNav variant="app" />
                <div className="max-w-md mx-auto pt-40 px-5 text-center text-white/70">
                    <div className="text-[20px] mb-3" style={{ fontFamily: "Geist, sans-serif", fontWeight: 300 }}>Devi effettuare il login</div>
                    <Link to="/login" className="eco-btn-primary inline-block mt-3">Accedi</Link>
                </div>
            </ModuleAccent>
        );
    }

    const t = stats?.totals || {};
    const series = stats?.daily_series || [];
    const maxSeries = Math.max(1, ...series.map((s) => s.count));

    return (
        <ModuleAccent module="aria" as="div" className="min-h-screen w-full overflow-x-hidden" style={baseStyle} data-testid="aria-dashboard">
            <TopNav variant="app" />

            {/* HERO */}
            <section className="px-5 md:px-10 pt-28 pb-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-2 mb-4 text-[11px] uppercase tracking-[0.22em] text-white/40" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    <Sparkles size={11} /> aria · dashboard owner
                </div>
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-white tracking-tight" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.9rem,3.8vw,3rem)", lineHeight: 1.05, letterSpacing: "-0.03em" }}>
                            Le tue conversazioni, <span style={{ fontWeight: 600 }}>già classificate</span>
                        </h1>
                        <p className="text-white/55 mt-1 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Lead arricchiti dall&apos;AI, intent in tempo reale, partner del Network e cosa richiamare oggi.
                        </p>
                    </div>
                    <PeriodSwitcher value={days} onChange={setDays} />
                </div>
            </section>

            {/* KPI GRID */}
            <section className="px-5 md:px-10 pb-6 max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
                <KpiCard label="Totali finestra" value={t.window ?? "—"} sub={`${t.all_time ?? 0} all-time`} testid="kpi-total" />
                <KpiCard label="Hot" value={t.hot ?? 0} icon={<Flame size={14} color="#F97316" />} accent="#F97316" testid="kpi-hot" />
                <KpiCard label="Warm" value={t.warm ?? 0} icon={<Sun size={14} color="#FBBF24" />} accent="#FBBF24" testid="kpi-warm" />
                <KpiCard label="Cold" value={t.cold ?? 0} icon={<Snowflake size={14} color="#7DD3FC" />} accent="#7DD3FC" testid="kpi-cold" />
                <KpiCard label="Goal raggiunti" value={t.goal_achieved ?? 0} icon={<Target size={14} color="#86EFAC" />} accent="#86EFAC" sub={`${t.conversion_rate_pct ?? 0}% conversion`} testid="kpi-goal" />
                <KpiCard label="Lead oggi" value={t.today ?? 0} icon={<Calendar size={14} color="#9EC3FF" />} accent="#9EC3FF" testid="kpi-today" />
            </section>

            {/* SPARK + NETWORK */}
            <section className="px-5 md:px-10 pb-6 max-w-7xl mx-auto grid md:grid-cols-3 gap-3">
                <Panel title="Volume giornaliero" icon={<TrendingUp size={13} />} className="md:col-span-2">
                    <div className="flex items-end gap-1.5 h-24 mt-2" data-testid="spark">
                        {series.map((s, i) => {
                            const h = Math.max(2, Math.round((s.count / maxSeries) * 88));
                            return (
                                <div key={s.date} className="flex-1 flex flex-col items-center gap-1.5">
                                    <div title={`${s.count} il ${s.date}`} className="w-full rounded-t-sm transition" style={{ height: `${h}px`, background: s.count ? "linear-gradient(180deg, #4F8BFF, rgba(43,90,255,0.4))" : "rgba(255,255,255,0.05)" }} />
                                    <span className="text-[9px] text-white/35" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.date.slice(5)}</span>
                                </div>
                            );
                        })}
                    </div>
                </Panel>
                <NetworkPanel net={stats?.network} />
            </section>

            {/* DA RICHIAMARE OGGI */}
            <section className="px-5 md:px-10 pb-6 max-w-7xl mx-auto">
                <SectionTitle title="Da richiamare oggi" sub={`${stats?.callbacks_today?.length || 0} lead hot non ancora contattati (ultimi 7gg)`} icon={<Phone size={13} />} />
                {(!stats?.callbacks_today || stats.callbacks_today.length === 0) ? (
                    <EmptyHint icon={<CheckCircle2 size={22} className="text-emerald-400/70" />} msg="Nessun lead da richiamare. Tutto sotto controllo." testid="callbacks-empty" />
                ) : (
                    <div className="space-y-2.5" data-testid="callbacks-list">
                        {stats.callbacks_today.map((c) => (
                            <CallbackCard key={c.lead_id} lead={c} onMark={() => markContacted(c.lead_id)} busy={contacting === c.lead_id} />
                        ))}
                    </div>
                )}
            </section>

            {/* TOP TAGS + FUTURE NEEDS + CATEGORIES */}
            <section className="px-5 md:px-10 pb-6 max-w-7xl mx-auto grid md:grid-cols-3 gap-3">
                <TopList title="Top tag" subtitle={`Cosa emerge dalle conversazioni`} items={stats?.top_tags || []} onPick={(v) => setTagFilter(v)} active={tagFilter} icon={<Tag size={12} />} color="#9EC3FF" testid="top-tags" />
                <TopList title="Bisogni futuri" subtitle={`Network matching seed`} items={stats?.top_future_needs || []} icon={<Network size={12} />} color="#FBBF24" testid="top-needs" />
                <TopList title="Categorie lead" subtitle={`Tassonomia AI`} items={stats?.top_categories || []} icon={<Inbox size={12} />} color="#A5F3FC" testid="top-categories" />
            </section>

            {/* LEAD LIST WITH FILTERS */}
            <section className="px-5 md:px-10 pb-24 max-w-7xl mx-auto">
                <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                    <SectionTitle title="Tutti i lead" sub={`${leads.length} risultati con i filtri attivi`} icon={<Inbox size={13} />} inline />
                    <FilterBar
                        intent={intentFilter} setIntent={setIntentFilter}
                        tag={tagFilter} setTag={setTagFilter}
                        goalOnly={goalOnly} setGoalOnly={setGoalOnly}
                        onClear={clearFilters}
                    />
                </div>

                {leadsLoading ? (
                    <div className="text-white/40 text-[13px] py-6" style={{ fontFamily: "Outfit, sans-serif" }}>Caricamento…</div>
                ) : leads.length === 0 ? (
                    <EmptyHint icon={<Inbox size={22} className="text-white/30" />} msg="Nessun lead con questi filtri." testid="leads-empty" />
                ) : (
                    <div className="space-y-2" data-testid="leads-list">
                        {leads.map((l) => (
                            <CompactLeadRow key={l.lead_id} lead={l} onPickTag={setTagFilter} />
                        ))}
                    </div>
                )}
            </section>

            <Footer variant="hub" />
            <style>{dashCSS}</style>
        </ModuleAccent>
    );
}

// =============== Components ===============

function PeriodSwitcher({ value, onChange }) {
    const opts = [
        { v: 1, label: "Oggi" },
        { v: 7, label: "7gg" },
        { v: 30, label: "30gg" },
        { v: 90, label: "90gg" },
    ];
    return (
        <div className="inline-flex p-1 rounded-full" style={{ background: "rgba(15,18,36,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
            {opts.map((o) => (
                <button
                    key={o.v}
                    type="button"
                    onClick={() => onChange(o.v)}
                    className="px-3 py-1.5 text-[11.5px] rounded-full transition"
                    style={{
                        background: value === o.v ? "var(--mod-accent-soft)" : "transparent",
                        color: value === o.v ? "var(--mod-accent)" : "rgba(255,255,255,0.55)",
                        fontFamily: "JetBrains Mono, monospace",
                        letterSpacing: "0.1em",
                    }}
                    data-testid={`period-${o.v}`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function KpiCard({ label, value, icon, sub, accent, testid }) {
    return (
        <motion.div initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl p-3.5" style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/45 mb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {icon || null} {label}
            </div>
            <div className="text-white text-[26px] leading-none" style={{ fontFamily: "Geist, sans-serif", fontWeight: 300, color: accent || "#fff" }}>{value}</div>
            {sub && <div className="text-white/35 text-[10px] mt-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>{sub}</div>}
        </motion.div>
    );
}

function Panel({ title, icon, children, className }) {
    return (
        <div className={`rounded-2xl p-4 ${className || ""}`} style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.2em] text-white/45 mb-1" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {icon} {title}
            </div>
            {children}
        </div>
    );
}

function NetworkPanel({ net }) {
    if (!net) return <Panel title="Network Aria" icon={<Network size={13} />}><div className="text-white/40 text-[12px] mt-2">—</div></Panel>;
    if (!net.is_partner) {
        return (
            <Panel title="Network Aria" icon={<Network size={13} />}>
                <div className="mt-3 text-white/55 text-[12.5px] leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Le tue istanze sono in modalità <b className="text-white">host</b>. Diventa partner per ricevere lead complementari da altri Aria.
                </div>
                <Link to="/aria-widget" className="inline-flex items-center gap-1.5 mt-3 text-[12px] text-[#9EC3FF] hover:text-white">Gestisci istanze <ChevronRight size={12} /></Link>
            </Panel>
        );
    }
    return (
        <Panel title="Network Aria · partner" icon={<Network size={13} />}>
            <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                    <div className="text-white text-[24px] leading-none" style={{ fontFamily: "Geist, sans-serif", fontWeight: 300, color: "#9EC3FF" }} data-testid="network-mentions">{net.inbound_mentions}</div>
                    <div className="text-white/45 text-[10px] mt-1" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em" }}>menzioni ricevute</div>
                </div>
                <div>
                    <div className="text-white text-[24px] leading-none" style={{ fontFamily: "Geist, sans-serif", fontWeight: 300 }}>{net.instances_partner}</div>
                    <div className="text-white/45 text-[10px] mt-1" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em" }}>istanze partner</div>
                </div>
            </div>
            {net.recent_mentions?.length > 0 && (
                <div className="mt-3 space-y-1">
                    <div className="text-[10px] text-white/35" style={{ fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: "0.16em" }}>menzioni recenti</div>
                    {net.recent_mentions.slice(0, 3).map((m, i) => (
                        <div key={i} className="text-[11.5px] text-white/65 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                            <span className="text-emerald-400">{m.from_site_name || "?"}</span> → <span className="text-white/80">{m.to_site_name}</span> · {m.matched_category}
                        </div>
                    ))}
                </div>
            )}
        </Panel>
    );
}

function SectionTitle({ title, sub, icon, inline }) {
    return (
        <div className={inline ? "" : "mb-3"}>
            <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] text-white/40 mb-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                {icon} {title}
            </div>
            <div className="text-white text-[14px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 400 }}>{sub}</div>
        </div>
    );
}

function CallbackCard({ lead, onMark, busy }) {
    return (
        <motion.div initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl p-4 transition hover:bg-white/[0.025]" style={{ background: "rgba(249,115,22,0.05)", border: "1px solid rgba(249,115,22,0.25)" }} data-testid={`callback-${lead.lead_id}`}>
            <div className="flex items-start gap-3 flex-wrap md:flex-nowrap">
                <div className="flex-none w-14 grid place-items-center rounded-xl py-2.5" style={{ background: "rgba(249,115,22,0.12)", border: "1px solid rgba(249,115,22,0.4)" }}>
                    <Flame size={15} color="#F97316" />
                    <span className="text-[9px] mt-1 tracking-[0.2em] text-[#F97316]" style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 600 }}>HOT</span>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-[14px] truncate" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{lead.name || "(senza nome)"}</span>
                        {lead.goal_achieved && (
                            <span className="text-[9.5px] text-emerald-400 px-1.5 py-0.5 rounded" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", fontFamily: "JetBrains Mono, monospace" }}>goal ✓</span>
                        )}
                    </div>
                    <div className="text-white/65 text-[12.5px] mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {[lead.email, lead.phone].filter(Boolean).join(" · ") || "—"}
                    </div>
                    {lead.summary && (
                        <p className="text-white/55 text-[12px] mt-1.5 leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>“{lead.summary}”</p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-none">
                    {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-white transition hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #F97316, #EA580C)", fontFamily: "Geist, sans-serif" }} data-testid={`call-${lead.lead_id}`}>
                            <Phone size={12} /> Chiama
                        </a>
                    )}
                    <button type="button" onClick={onMark} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] text-white/85 hover:text-white transition disabled:opacity-50" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid={`mark-contacted-${lead.lead_id}`}>
                        {busy ? "…" : <><CheckCircle2 size={12} /> Contattato</>}
                    </button>
                </div>
            </div>
        </motion.div>
    );
}

function TopList({ title, subtitle, items, onPick, active, icon, color, testid }) {
    if (!items || items.length === 0) {
        return (
            <Panel title={title} icon={icon}>
                <div className="text-white/40 text-[12px] mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>Nessun dato nel periodo.</div>
            </Panel>
        );
    }
    const max = Math.max(1, ...items.map((i) => i.count));
    return (
        <Panel title={title} icon={icon}>
            <div className="text-white/40 text-[10.5px] mb-2 mt-0.5" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em" }}>{subtitle}</div>
            <div className="space-y-1.5" data-testid={testid}>
                {items.slice(0, 10).map((it) => {
                    const pct = Math.round((it.count / max) * 100);
                    const isActive = onPick && active === it.value;
                    return (
                        <button
                            key={it.value}
                            type="button"
                            onClick={() => onPick && onPick(isActive ? "" : it.value)}
                            disabled={!onPick}
                            className="w-full text-left group disabled:cursor-default"
                            data-testid={`${testid}-${it.value}`}
                        >
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                                <span className="text-[12px] truncate" style={{ color: isActive ? color : "rgba(255,255,255,0.85)", fontFamily: "Outfit, sans-serif", fontWeight: isActive ? 600 : 400 }}>{it.value}</span>
                                <span className="text-[10.5px] flex-none" style={{ color: "rgba(255,255,255,0.45)", fontFamily: "JetBrains Mono, monospace" }}>{it.count}</span>
                            </div>
                            <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
                                <div className="h-full transition-all" style={{ width: `${pct}%`, background: isActive ? color : `${color}55` }} />
                            </div>
                        </button>
                    );
                })}
            </div>
        </Panel>
    );
}

function FilterBar({ intent, setIntent, tag, setTag, goalOnly, setGoalOnly, onClear }) {
    const anyActive = intent || tag || goalOnly;
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <Filter size={13} className="text-white/40" />
            <select value={intent} onChange={(e) => setIntent(e.target.value)} className="dash-select" data-testid="filter-intent">
                <option value="">Tutti gli intent</option>
                <option value="hot">Hot</option>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
            </select>
            {tag && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] text-[#9EC3FF]" style={{ background: "rgba(79,139,255,0.12)", border: "1px solid rgba(79,139,255,0.35)" }} data-testid="filter-tag-active">
                    #{tag}
                    <button type="button" onClick={() => setTag("")} className="text-[#9EC3FF]/70 hover:text-white"><X size={11} /></button>
                </span>
            )}
            <label className="inline-flex items-center gap-1.5 text-[11.5px] text-white/65 cursor-pointer">
                <input type="checkbox" checked={goalOnly} onChange={(e) => setGoalOnly(e.target.checked)} style={{ accentColor: "#4F8BFF" }} data-testid="filter-goal-only" />
                Solo goal raggiunti
            </label>
            {anyActive && (
                <button type="button" onClick={onClear} className="text-[11px] text-white/40 hover:text-white inline-flex items-center gap-1" data-testid="filter-clear">
                    Reset <X size={10} />
                </button>
            )}
        </div>
    );
}

function CompactLeadRow({ lead, onPickTag }) {
    const intent = (lead.intent_level || "").toLowerCase();
    const map = {
        hot: { color: "#F97316", Icon: Flame },
        warm: { color: "#FBBF24", Icon: Sun },
        cold: { color: "#7DD3FC", Icon: Snowflake },
    };
    const m = map[intent] || { color: "rgba(255,255,255,0.4)", Icon: Snowflake };
    const I = m.Icon;
    const created = lead.created_at ? new Date(lead.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
    return (
        <div className="rounded-xl p-3 transition hover:bg-white/[0.025]" style={{ background: "rgba(15,18,36,0.5)", border: "1px solid rgba(255,255,255,0.05)" }} data-testid={`dash-lead-${lead.lead_id}`}>
            <div className="flex items-start gap-3">
                <div className="flex-none mt-0.5" title={intent}><I size={13} color={m.color} /></div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-[13px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{lead.name || "—"}</span>
                        <span className="text-white/45 text-[11.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>{lead.email || lead.phone || ""}</span>
                        {lead.contacted_at && <span className="text-[9.5px] text-emerald-400 px-1.5 py-0.5 rounded" style={{ background: "rgba(16,185,129,0.1)", fontFamily: "JetBrains Mono, monospace" }}>contattato</span>}
                        {lead.goal_achieved && <span className="text-[9.5px] text-emerald-400 px-1.5 py-0.5 rounded" style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", fontFamily: "JetBrains Mono, monospace" }}>goal</span>}
                        <span className="ml-auto text-white/30 text-[10px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{created}</span>
                    </div>
                    {lead.summary && <p className="text-white/55 text-[11.5px] mt-1 line-clamp-2" style={{ fontFamily: "Outfit, sans-serif" }}>{lead.summary}</p>}
                    {(lead.tags?.length || lead.category) && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                            {lead.category && (
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] text-[#9EC3FF]" style={{ background: "rgba(79,139,255,0.1)", border: "1px solid rgba(79,139,255,0.25)" }}>{lead.category}</span>
                            )}
                            {(lead.tags || []).slice(0, 4).map((t, i) => (
                                <button key={i} type="button" onClick={() => onPickTag && onPickTag(t)} className="inline-flex px-1.5 py-0.5 rounded text-[10px] text-white/75 hover:text-white transition" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                                    #{t}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function EmptyHint({ icon, msg, testid }) {
    return (
        <div className="rounded-2xl p-8 text-center" style={{ background: "rgba(15,18,36,0.4)", border: "1px solid rgba(255,255,255,0.05)" }} data-testid={testid}>
            <div className="flex justify-center mb-2">{icon}</div>
            <div className="text-white/55 text-[13px]" style={{ fontFamily: "Outfit, sans-serif" }}>{msg}</div>
        </div>
    );
}

const dashCSS = `
.dash-select{padding:5px 10px;border-radius:999px;border:1px solid rgba(255,255,255,0.1);background:rgba(15,18,36,0.6);color:#fff;font-family:'JetBrains Mono',monospace;font-size:11.5px;letter-spacing:0.1em;text-transform:uppercase;cursor:pointer;}
.dash-select:focus{outline:none;border-color:rgba(79,139,255,0.55);}
.dash-select option{background:#0F1224;color:#fff;font-family:'Outfit',sans-serif;text-transform:none;letter-spacing:0;}
`;
